import { isPlatformBrowser } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    HostListener,
    Inject,
    OnInit,
    PLATFORM_ID,
} from "@angular/core";
import {
    NavigationEnd,
    RouteConfigLoadEnd,
    RouteConfigLoadStart,
    Router,
    RoutesRecognized,
} from "@angular/router";
import { Observable } from "rxjs";
import { Globals } from "../../app.globals";
import { NavService } from "../../services/nav.service";
import { ProjectsService } from "../../services/projects/projects.service";
import { FormsService } from "../../services/settings/forms.service";
import { ClientService } from "../../services/shared/client.service";
import { DateTimeService } from "../../services/shared/datetime.service";
import { LoaderService } from "../../services/shared/loader-service";
import { UserService } from "../../services/user.service";

@Component({
        standalone: false,
    selector: "app",
    templateUrl: "./app.component.html",
    providers: [ProjectsService, FormsService, ClientService, DateTimeService],
})
export class AppComponent implements OnInit {
    private projectId: number = null;
    private navService: NavService;
    private router: Router;
    private clientService: ClientService;
    private projectsService: ProjectsService;
    private formsService: FormsService;
    userService: UserService;
    loaderService: LoaderService;
    globals: Globals;

    private hasAlreadySynced: boolean = false;

    constructor(
        @Inject(PLATFORM_ID) private platformId: object,
        userService: UserService,
        navService: NavService,
        router: Router,
        clientService: ClientService,
        projectsService: ProjectsService,
        formsService: FormsService,
        loaderService: LoaderService,
        globals: Globals,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.userService = userService;
        this.navService = navService;
        this.router = router;
        this.clientService = clientService;
        this.projectsService = projectsService;
        this.formsService = formsService;
        this.loaderService = loaderService;
        this.globals = globals;
    }

    ngOnInit() {
        if (isPlatformBrowser(this.platformId)) {
            this.globals.scrollbarWidth = this.GetScrollbarWidth();
            this.router.events.subscribe((event: any) => {
                if (event instanceof RouteConfigLoadStart) {
                    this.globals.showSpinner();
                } else if (event instanceof RouteConfigLoadEnd) {
                    this.globals.hideSpinner();
                } else if (event instanceof NavigationEnd) {
                    this.onNavigationEnd(event);
                } else if (event instanceof RoutesRecognized) {
                    this.onRoutesRecognized(event);
                }
            });
        }
    }

    @HostListener("window:online", ["$event"])
    onOnline(event) {
        this.globals.hideSpinner();
        this.globals.spinnerMessage = null;
    }

    @HostListener("window:offline", ["$event"])
    onOffline(event) {
        this.globals.showSpinner();
        this.globals.spinnerMessage =
            "You've lost internet connection, please do not navigate away from this page, any unsaved data will be lost.<br />Awaiting connection to be restored.";
    }

    @HostListener("window:resize", ["$event"])
    onResize() {
        this.globals.scrollbarWidth = this.GetScrollbarWidth();
    }

    GetScrollbarWidth(): number {
        var scrollDiv = document.createElement("div");

        scrollDiv.className = "scrollbar-measure";

        document.body.appendChild(scrollDiv);

        var scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;

        document.body.removeChild(scrollDiv);

        return scrollbarWidth;
    }

    private onNavigationEnd(event: NavigationEnd) {
        let urlParts = document.location.pathname
            .toLowerCase()
            .split("/")
            .filter((x) => x);

        if (urlParts.length > 1) {
            this.projectId = +urlParts[1] || null;
        } else this.projectId = null;

        if (
            this.projectId &&
            (!this.navService.menu.summary ||
                this.navService.menu.summary.id != this.projectId)
        )
            this.projectsService
                .getSummary(this.projectId)
                .subscribe((data) => {
                    data.id = this.projectId;
                    this.navService.setProject(data);
                });
        else if (!this.projectId) this.navService.setProject(null);

        this.navService.displayBreadcrumbs = true;

        let isLoginPage = false;

        if (!this.projectId) this.navService.loadDefaultMenu();

        switch (urlParts[0]) {
            case "offline":
                this.navService.loadOfflineMenu(this.projectId);
                break;
            case "projects":
                if (this.projectId) {
                    if (urlParts.length > 2 && urlParts[2] === "edit")
                        this.navService.loadProjectsEditMenu(this.projectId);
                    else this.navService.loadProjectsViewMenu(this.projectId);
                } else {
                    this.navService.loadProjectsAdminView();
                }
                break;
            case "locations":
                if (this.projectId) {
                    if (urlParts.length > 2 && urlParts[2] === "edit")
                        this.navService.loadLocationsEditMenu(this.projectId);
                    else this.navService.loadLocationsViewMenu(this.projectId);
                }
                break;
            case "qs":
                if (!this.projectId) this.navService.loadQSAdminMenu();
                else {
                    if (urlParts.length > 2 && urlParts[2] == "edit")
                        this.navService.loadQSEditMenu(this.projectId);
                    else this.navService.loadQSViewMenu(this.projectId);
                }
                break;
            case "estimating":
                if (!this.projectId) this.navService.loadEstimatingAdminMenu();
                else {
                    if (urlParts.length > 2 && urlParts[2] == "edit")
                        this.navService.loadEstimatingEditMenu(this.projectId);
                    else this.navService.loadEstimatingViewMenu(this.projectId);
                }
                break;
            case "settings":
                this.navService.loadSettingsMenu();
                break;
            case "procurement":
                if (urlParts[1] == "materials") {
                    this.navService.loadMaterialMenu();
                } else if (urlParts[1] == "plant") {
                    this.navService.loadPlantMenu();
                }
                break;
            case "quality-assurance":
                if (!this.projectId)
                    this.navService.loadQualityAssuranceAdminMenu();
                else {
                }
                break;
            case "health-and-safety":
                if (!this.projectId)
                    this.navService.loadHealthAndSafetyAdminMenu();
                else {
                }
                break;
            case "unauthorized":
                this.navService.loadUnauthorizedMenu();
                break;
            case "login":
            case "logout":
            case "":
            case null:
            case undefined:
                isLoginPage = true;
                this.navService.displayBreadcrumbs = false;
                break;
            default:
                this.navService.displayBreadcrumbs = false;
                break;
        }

        if (urlParts.length == 0) {
            isLoginPage = true;
            this.navService.displayBreadcrumbs = false;
        }

        this.navService.setActivePage(event.url);
        this.formsService.projectId = this.projectId;

        if (!isLoginPage) {
            if (!this.hasAlreadySynced) {
                this.hasAlreadySynced = true;
            }

            if (!this.navService.menu.client) {
                var clientServiceCall: Observable<any>;

                if (this.projectId != null)
                    clientServiceCall = this.clientService.get(this.projectId);
                else clientServiceCall = this.clientService.get();

                clientServiceCall.subscribe((data) => {
                    this.navService.setClient(data);

                    this.loaderService.isLoaderVisible = false;

                    if (!this.changeDetectorRef["destroyed"])
                        this.changeDetectorRef.detectChanges();
                });
            }
        } else {
            this.navService.setClient(null);
        }

        this.loaderService.isLoaderVisible = false;
    }

    private onRoutesRecognized(event: RoutesRecognized) {}
}
